import Vue from 'vue'
import Vuex from 'vuex'
import finger from 'fingerprintjs2'

import { IRoom, IRoomForm } from '@/models/IRoom'
import { api } from '@/shared/api'
import { Actions } from '@/shared/Actions'
import { Admin } from '@/models/Admin'
import { SingleActions } from '../shared/Actions'
import { IStudent } from '@/models/IStudent'
import { setMode } from '@/mode'
import { CONNECTION_STATUS } from '@/shared/consts'
//@ts-ignore
import { createHash } from 'crypto'

Vue.use(Vuex)

const store = new Vuex.Store({
  state: {
    rooms: [] as IRoom[],
    isProcessing: {
      [SingleActions.FETCH_ALL_ROOMS]: false as boolean,
      [SingleActions.CREATE_ROOM]: false as boolean,
      [SingleActions.FETCH_ALL_ADMINS]: false as boolean,
      [SingleActions.UPDATE_CONFIG]: false as boolean,
      [SingleActions.FETCH_CONFIG]: false as boolean,

      [Actions.DELETE_ROOM]: {} as any,
      [Actions.HIDE_ROOM]: {} as any,
      [Actions.UPDATE_ROOM]: {} as any,
      [Actions.REGISTER_STUDENT]: {} as any,
      [Actions.REMOVE_STUDENT]: {} as any,
      [Actions.RESERVE_ROOM]: {} as any,

      [Actions.ADMIN_STUDENT_ADD]: {} as any,
      [Actions.ADMIN_STUDENT_EDIT]: {} as any,
      [Actions.ADMIN_STUDENT_REMOVE]: {} as any
    },
    admins: [] as Admin[],
    user: {
      uuid: '' as string,
      meta: '' as string,
      data: {
        name: null as string | null,
        imageUrl: null as string | null,
        email: null as string | null
      }
    },
    config: {
      title: null as string | null,
      mode: null as string | null,
      registrationsUrl: null as string | null
    },
    clientsCount: 0,
    connectionStatus: CONNECTION_STATUS.RECONNECTING
  },
  mutations: {
    setConnectionStatus(state, status) {
      state.connectionStatus = status
    },
    setRooms(state, rooms: IRoom[]) {
      state.rooms = rooms
    },
    pushRoom(state, room: IRoom) {
      state.rooms.push(room)
    },
    removeRoom(state, room: IRoom) {
      state.rooms = state.rooms.filter((it: IRoom) => it._id !== room._id)
    },

    updateRoomStudents(state, updatedRoom: IRoom) {
      const room = state.rooms.find(it => it._id === updatedRoom._id)
      if (room) {
        room.students = updatedRoom.students
      }
    },
    updateRoomReservation(state, updatedRoom: IRoom) {
      const room = state.rooms.find(it => it._id === updatedRoom._id)
      if (room) {
        room.reservedMeta = updatedRoom.reservedMeta
        room.reservedUntil = updatedRoom.reservedUntil
      }
    },
    setUserUUID(state, uuid) {
      state.user.uuid = uuid
    },
    setUserUUIDMeta(state, hash) {
      state.user.meta = hash
    },
    setClientsCount(state, count) {
      state.clientsCount = count
    },
    setUserData(state, userData) {
      state.user.data = userData
    },
    setAdmins(state, admins) {
      state.admins = admins
    },
    setConfig(state, config) {
      state.config = config
    },

    updateProcessing(
      state,
      { type, isProcessing }: { type: SingleActions; isProcessing: boolean }
    ) {
      state.isProcessing[type] = isProcessing
    },
    updateActionProcessing(
      state,
      { type, id, isProcessing }: { type: Actions; id: any; isProcessing: boolean }
    ) {
      Vue.set(state.isProcessing[type], id, isProcessing)
    }
  },
  actions: {
    async fetchUserData({ commit }) {
      const result = await api.admins.findMe()
      commit('setUserData', result.data)
    },

    async removeAdmin({ dispatch }, email) {
      await api.admins.remove(email)
      dispatch(SingleActions.FETCH_ALL_ADMINS)
    },

    async acceptAdmin({ dispatch }, email) {
      await api.admins.accept(email)
      dispatch(SingleActions.FETCH_ALL_ADMINS)
    },

    async [SingleActions.UPDATE_CONFIG]({ dispatch }, config) {
      _setSingleProcessing(SingleActions.UPDATE_CONFIG)
      try {
        await api.config.set(config)
        dispatch(SingleActions.FETCH_CONFIG)
      } finally {
        _setSingleDone(SingleActions.UPDATE_CONFIG)
      }
    },

    async [SingleActions.FETCH_CONFIG]({ commit }) {
      _setSingleProcessing(SingleActions.FETCH_CONFIG)
      try {
        const result = await api.config.get()
        commit('setConfig', result.data)
        setMode(`pl-${result.data.mode}`)
      } finally {
        _setSingleDone(SingleActions.FETCH_CONFIG)
      }
    },

    async [SingleActions.FETCH_ALL_ADMINS]({ commit }) {
      _setSingleProcessing(SingleActions.FETCH_ALL_ADMINS)
      try {
        const result = await api.admins.fetchAll()
        commit('setAdmins', result.data)
      } finally {
        _setSingleDone(SingleActions.FETCH_ALL_ADMINS)
      }
    },

    async [Actions.RESERVE_ROOM]({ state }, { roomId }) {
      _setActionProcessing(roomId, Actions.RESERVE_ROOM)
      try {
        await api.students.reserve_room(roomId, state.user.uuid)
      } finally {
        _setActionDone(roomId, Actions.RESERVE_ROOM)
      }
    },
    async [Actions.REMOVE_STUDENT]({ state }, { roomId, student }) {
      _setActionProcessing(roomId + student, Actions.REMOVE_STUDENT)
      try {
        await api.students.remove_student(roomId, student._id, state.user.uuid)
      } finally {
        _setActionDone(roomId + student, Actions.REMOVE_STUDENT)
      }
    },
    async [Actions.REGISTER_STUDENT]({ state }, { roomId, student }) {
      _setActionProcessing(roomId, Actions.REGISTER_STUDENT)
      try {
        await api.students.register_student(roomId, student, state.user.uuid)
      } finally {
        _setActionDone(roomId, Actions.REGISTER_STUDENT)
      }
    },
    async [SingleActions.FETCH_ALL_ROOMS]({ commit }) {
      _setSingleProcessing(SingleActions.FETCH_ALL_ROOMS)
      try {
        const response = await api.rooms.findAll()
        commit('setRooms', response.data)
      } finally {
        _setSingleDone(SingleActions.FETCH_ALL_ROOMS)
      }
    },
    async [SingleActions.CREATE_ROOM]({ commit }, room: IRoomForm) {
      _setSingleProcessing(SingleActions.CREATE_ROOM)
      try {
        const response = await api.rooms.create(room)
        commit('pushRoom', response.data)
      } finally {
        _setSingleDone(SingleActions.CREATE_ROOM)
      }
    },
    async [Actions.DELETE_ROOM]({ commit }, id: string) {
      _setActionProcessing(id, Actions.DELETE_ROOM)
      try {
        const response = await api.rooms.delete(id)
        commit('removeRoom', response.data)
      } finally {
        _setActionDone(id, Actions.DELETE_ROOM)
      }
    },
    async [Actions.UPDATE_ROOM]({ dispatch }, { id, payload }) {
      _setActionProcessing(id, Actions.UPDATE_ROOM)
      try {
        await api.rooms.update(id, payload)
        dispatch(SingleActions.FETCH_ALL_ROOMS)
      } finally {
        _setActionDone(id, Actions.UPDATE_ROOM)
      }
    },
    async [Actions.HIDE_ROOM]({ dispatch }, { id, hide }) {
      _setActionProcessing(id, Actions.HIDE_ROOM)
      try {
        await api.rooms.hide(id, { hide })
        dispatch(SingleActions.FETCH_ALL_ROOMS)
      } finally {
        _setActionDone(id, Actions.HIDE_ROOM)
      }
    },

    async [Actions.ADMIN_STUDENT_ADD]({}, { roomId, student }) {
      _setActionProcessing(roomId + student, Actions.ADMIN_STUDENT_ADD)
      try {
        await api.admin_students.register_by_admin(student, roomId)
      } finally {
        _setActionDone(roomId + student, Actions.ADMIN_STUDENT_ADD)
      }
    },
    async [Actions.ADMIN_STUDENT_EDIT]({}, { roomId, studentId, student }) {
      _setActionProcessing(roomId + studentId, Actions.ADMIN_STUDENT_EDIT)
      try {
        await api.admin_students.update_by_admin(student, roomId, studentId)
      } finally {
        _setActionDone(roomId + studentId, Actions.ADMIN_STUDENT_EDIT)
      }
    },
    async [Actions.ADMIN_STUDENT_REMOVE]({}, { roomId, studentId }) {
      _setActionProcessing(roomId + studentId, Actions.ADMIN_STUDENT_REMOVE)
      try {
        await api.admin_students.delete_by_admin(roomId, studentId)
      } finally {
        _setActionDone(roomId + studentId, Actions.ADMIN_STUDENT_REMOVE)
      }
    },

    countUserFingerPrint({ commit }) {
      setTimeout(() => {
        finger.get(components => {
          const extractedValues = components.map(it => it.value)
          const hash = finger.x64hash128(extractedValues.join(''), 31)
          commit('setUserUUID', hash)
          commit(
            'setUserUUIDMeta',
            createHash('sha256')
              .update(hash, 'utf8')
              .digest('hex')
          )
        })
      }, 500)
    }
  },
  getters: {
    duplicatedStudents: state => {
      const allStudentsWithRooms = state.rooms.flatMap((room: IRoom) =>
        room.students.map(it => ({ ...it, room }))
      )

      const duplicatedStudents: Array<IStudent & { appearsIn: IRoom[] }> = []
      for (const student of allStudentsWithRooms) {
        const allStudentEntries = allStudentsWithRooms.filter(
          (other: IStudent) =>
            other.name.toLowerCase() === student.name.toLowerCase() &&
            other.surname.toLowerCase() === student.surname.toLowerCase()
        )
        if (allStudentEntries.length > 1) {
          if (
            !duplicatedStudents.find(
              it =>
                it.name.toLowerCase() === student.name.toLowerCase() &&
                it.surname.toLowerCase() === student.surname.toLowerCase()
            )
          ) {
            duplicatedStudents.push({ ...student, appearsIn: allStudentEntries.map(it => it.room) })
          }
        }
      }

      return duplicatedStudents
    }
  }
})

function _setSingleProcessing(type: SingleActions) {
  store.commit('updateProcessing', { type, isProcessing: true })
}

function _setSingleDone(type: SingleActions) {
  store.commit('updateProcessing', { type, isProcessing: false })
}

function _setActionProcessing(id: any, type: Actions) {
  store.commit('updateActionProcessing', { type, id, isProcessing: true })
}

function _setActionDone(id: any, type: Actions) {
  store.commit('updateActionProcessing', { type, id, isProcessing: false })
}

export default store
